import { useState, useEffect } from 'react';
import { Save, Globe, Mail, Phone, MapPin, Facebook, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { getSettings, updateSettings } from '../lib/api';

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_title_es: '',
    hero_subtitle: '',
    hero_subtitle_es: '',
    contact_email: '',
    contact_whatsapp: '',
    contact_address: '',
    facebook_url: '',
    instagram_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        setSettings(response.data);
      } catch (error) {
        toast.error('Error loading settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-settings">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Ajustes</h1>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold px-6 h-11"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>

      {/* Hero Section Settings */}
      <div className="admin-card p-6 space-y-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Globe className="w-5 h-5 text-cyan-400" />
          Hero Section
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm text-slate-400">Title (EN)</label>
            <Textarea
              value={settings.hero_title}
              onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              data-testid="settings-hero-title"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm text-slate-400">Title (ES)</label>
            <Textarea
              value={settings.hero_title_es}
              onChange={(e) => setSettings({ ...settings, hero_title_es: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm text-slate-400">Subtitle (EN)</label>
            <Textarea
              value={settings.hero_subtitle}
              onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm text-slate-400">Subtitle (ES)</label>
            <Textarea
              value={settings.hero_subtitle_es}
              onChange={(e) => setSettings({ ...settings, hero_subtitle_es: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
        </div>
      </div>

      {/* Contact Settings */}
      <div className="admin-card p-6 space-y-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Phone className="w-5 h-5 text-cyan-400" />
          Contact Information
        </h2>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm text-slate-400 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </label>
            <Input
              type="email"
              value={settings.contact_email}
              onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              data-testid="settings-email"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-slate-400 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              WhatsApp
            </label>
            <Input
              type="tel"
              value={settings.contact_whatsapp}
              onChange={(e) => setSettings({ ...settings, contact_whatsapp: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              data-testid="settings-whatsapp"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-slate-400 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Address
            </label>
            <Input
              value={settings.contact_address}
              onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              data-testid="settings-address"
            />
          </div>
        </div>
      </div>

      {/* Social Media */}
      <div className="admin-card p-6 space-y-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Globe className="w-5 h-5 text-cyan-400" />
          Redes Sociales
        </h2>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm text-slate-400 flex items-center gap-2">
              <Facebook className="w-4 h-4" />
              Facebook URL
            </label>
            <Input
              type="url"
              placeholder="https://facebook.com/ferrtours"
              value={settings.facebook_url || ''}
              onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              data-testid="settings-facebook"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-slate-400 flex items-center gap-2">
              <Instagram className="w-4 h-4" />
              Instagram URL
            </label>
            <Input
              type="url"
              placeholder="https://instagram.com/ferrtours"
              value={settings.instagram_url || ''}
              onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              data-testid="settings-instagram"
            />
          </div>
        </div>
      </div>


      {/* Save Button (Bottom) */}
      <div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold h-12 text-base"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>
    </div>
  );
}
